import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Navigate, Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { AgentSectionsNav } from '@/components/projects'
import {
  useAuth,
  useAgent,
  useAgents,
  useCreateAgent,
  useCreateAgentVersion,
  useProject,
  useSaveAgentMemoryEntries,
  useUnsavedChangesGuard,
  useUpdateAgent,
} from '@/hooks'
import type { AgentDetailContext, PendingAgentMemoryEntry } from '@/hooks'
import { PENDING_MEMORY_CONFLICT_MESSAGE } from '@/hooks/useAgentDetailContext'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage, getValidationErrorMessage } from '@/lib/validationErrors'
import { AGENT_STARTER_YAML, AGENT_YAML_MAX, listUnknownAgentYamlKeys, parseAgentYaml } from '@/lib/agentYaml'
import { downloadBlob } from '@/lib/downloadBlob'
import { Permissions } from '@/lib/permissions'
import { toSlug } from '@/lib/slug'
import { KEBAB_CASE_REGEX } from '@/types'
import type { AgentDetail } from '@/types'
import { toast } from 'sonner'

const NEUTRAL_BADGE_BORDER = 'rgba(118,124,150,1)'
const NEW_AGENT_ID = 'new'

interface CarriedAgentState {
  pendingMemories: PendingAgentMemoryEntry[]
}

function carriedPendingMemories(state: unknown): PendingAgentMemoryEntry[] {
  return (state as Partial<CarriedAgentState> | null)?.pendingMemories ?? []
}

export function AgentDetailPage() {
  const { agentId } = useParams()
  return <AgentDetailContent key={agentId} />
}

function AgentDetailContent() {
  const { projectId, agentId } = useParams()
  const navigate = useNavigate()
  const { pathname, state: locationState } = useLocation()
  const isNew = agentId === NEW_AGENT_ID
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const { data: agent, isLoading, isError } = useAgent(projectId!, isNew ? undefined : agentId)
  const { data: accessibleAgents = [] } = useAgents(projectId!, isNew)
  const { data: project } = useProject(projectId)
  const createAgent = useCreateAgent(projectId!)
  const createVersion = useCreateAgentVersion(projectId!)
  const updateAgent = useUpdateAgent(projectId!)
  const saveMemories = useSaveAgentMemoryEntries(projectId!)

  const [yamlDraft, setYamlDraft] = useState<string | null>(null)
  const [pendingMemories, setPendingMemories] = useState<PendingAgentMemoryEntry[]>(() => carriedPendingMemories(locationState))
  const [displayNameDraft, setDisplayNameDraft] = useState<string | null>(null)
  const [internalNameDraft, setInternalNameDraft] = useState<string | null>(null)
  const [internalNameError, setInternalNameError] = useState<string | null>(null)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)
  const [saveAttempted, setSaveAttempted] = useState(false)
  const saveInFlight = useRef(false)

  const savedAgent = agent ?? null
  const savedYaml = savedAgent?.yamlContent ?? AGENT_STARTER_YAML
  const editorYaml = yamlDraft ?? savedYaml
  const displayName = displayNameDraft ?? savedAgent?.displayName ?? ''
  const internalName = savedAgent?.name ?? internalNameDraft ?? toSlug(displayName)
  const description = descriptionDraft ?? savedAgent?.description ?? ''

  const parsedConfig = useMemo(() => {
    try {
      return parseAgentYaml(editorYaml)
    } catch {
      return null
    }
  }, [editorYaml])

  const unknownYamlKeys = useMemo(
    () => (parsedConfig ? listUnknownAgentYamlKeys(editorYaml) : []),
    [parsedConfig, editorYaml]
  )

  const trimmedDisplayName = displayName.trim()
  const trimmedInternalName = internalName.trim()
  const trimmedDescription = description.trim()
  const yamlChanged = editorYaml !== savedYaml
  const metadataChanged =
    trimmedDisplayName !== (savedAgent?.displayName ?? '') ||
    trimmedDescription !== (savedAgent?.description ?? '')
  const isDirty = yamlChanged || metadataChanged || internalNameDraft !== null || pendingMemories.length > 0
  const yamlTooLong = editorYaml.length > AGENT_YAML_MAX

  const missingRequiredFields = parsedConfig
    ? [
        parsedConfig.systemPrompt.trim() ? null : 'system prompt',
        parsedConfig.model.trim() ? null : 'model',
      ].filter((field): field is string => field !== null)
    : []

  const pendingMemoryConflict = pendingMemories.length > 0 && parsedConfig !== null && parsedConfig.memory !== 'project'

  const isSaving = createAgent.isPending || createVersion.isPending || updateAgent.isPending || saveMemories.isPending
  const nameClaimedByThisPage = createAgent.isPending || createAgent.isSuccess
  const internalNameTaken = !nameClaimedByThisPage && accessibleAgents.some(existing => existing.name === trimmedInternalName)

  let internalNameProblem: string | null = null
  if (savedAgent) internalNameProblem = null
  else if (!trimmedInternalName) internalNameProblem = 'Internal name is required.'
  else if (!KEBAB_CASE_REGEX.test(trimmedInternalName)) internalNameProblem = 'Internal name must be kebab-case (e.g. "backend-analyst").'
  else if (internalNameTaken) internalNameProblem = 'An agent with this name already exists in the project or as a global agent.'

  let validationError: string | null = null
  if (!trimmedDisplayName) validationError = 'Display name is required.'
  else if (internalNameProblem) validationError = internalNameProblem
  else if (internalNameError) validationError = internalNameError
  else if (!parsedConfig) validationError = "The YAML is invalid, so it can't be saved. Fix the syntax in the YAML tab."
  else if (yamlTooLong) validationError = `The YAML must not exceed ${AGENT_YAML_MAX} characters (currently ${editorYaml.length}).`
  else if (missingRequiredFields.length > 0) validationError = `Required before saving: ${missingRequiredFields.join(' and ')}.`
  else if (pendingMemoryConflict) validationError = PENDING_MEMORY_CONFLICT_MESSAGE

  let saveBlockedReason: string | null = null
  if (isSaving) saveBlockedReason = 'Saving…'
  else if (!isDirty) saveBlockedReason = 'No changes to save.'

  const agentsPath = `/projects/${projectId}/workflows/agents`
  const agentPath = `${agentsPath}/${agentId}`
  const pathnameRef = useRef(pathname)
  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])
  useEffect(() => {
    if (carriedPendingMemories(locationState).length > 0) navigate(pathname, { replace: true, state: null })
  }, [locationState, navigate, pathname])
  const currentSection = () =>
    pathnameRef.current.startsWith(`${agentPath}/`) ? pathnameRef.current.slice(agentPath.length + 1) : 'config'

  const guard = useUnsavedChangesGuard({
    isDirty: isDirty || isSaving,
    isNavigationAllowed: next => next.pathname === agentPath || next.pathname.startsWith(`${agentPath}/`),
  })

  const discardDrafts = () => {
    setYamlDraft(null)
    setDisplayNameDraft(null)
    setInternalNameDraft(null)
    setInternalNameError(null)
    setDescriptionDraft(null)
    setPendingMemories([])
    setSaveAttempted(false)
  }

  const persistPendingMemories = async (targetAgentId: string): Promise<PendingAgentMemoryEntry[]> => {
    const submitted = pendingMemories
    if (submitted.length === 0) return []
    const { failedKeys, firstError } = await saveMemories.mutateAsync({ agentId: targetAgentId, entries: submitted })
    const submittedKeys = new Set(submitted.map(entry => entry.key))
    setPendingMemories(prev => prev.filter(entry => !submittedKeys.has(entry.key) || failedKeys.includes(entry.key)))
    const failed = submitted.filter(entry => failedKeys.includes(entry.key))
    if (failed.length > 0) {
      const reason = extractValidationMessage(firstError) ?? getErrorMessage(firstError) ?? 'Failed to save memory'
      toast.error(`${failed.length} of ${submitted.length} memory entries weren't saved — they are still listed under Memory: ${reason}`)
    }
    return failed
  }

  const saveExisting = async (existing: AgentDetail): Promise<boolean> => {
    let metadataSaved = false
    try {
      if (metadataChanged) {
        await updateAgent.mutateAsync({
          id: existing.id,
          data: {
            displayName: trimmedDisplayName,
            ...(trimmedDescription ? { description: trimmedDescription } : {}),
          },
        })
        metadataSaved = true
        setDisplayNameDraft(prev => (prev === displayName ? null : prev))
        setDescriptionDraft(prev => (prev === description ? null : prev))
      }
      if (yamlChanged) {
        const response = await createVersion.mutateAsync({ id: existing.id, data: { yaml: editorYaml } })
        setYamlDraft(prev => (prev === editorYaml ? null : prev))
        toast.success(`Saved "${trimmedDisplayName}" as version ${response.version} — workflows pin a version, so the ones already running keep theirs.`)
      } else if (metadataChanged) {
        toast.success(`Saved "${trimmedDisplayName}"`)
      }
      return (await persistPendingMemories(existing.id)).length === 0
    } catch (error) {
      const reason = extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to save agent'
      toast.error(metadataSaved ? `Name and description were saved, but the configuration wasn't: ${reason}` : reason)
      return false
    }
  }

  const createNew = async (): Promise<{ agentId: string; failedMemories: PendingAgentMemoryEntry[] } | null> => {
    const created = await createAgent
      .mutateAsync({
        name: trimmedInternalName,
        displayName: trimmedDisplayName,
        yaml: editorYaml,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      })
      .catch((error: unknown) => {
        setInternalNameError(getValidationErrorMessage(error, 'Name'))
        toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create agent')
        return null
      })
    if (!created) return null
    toast.success(`Created "${trimmedDisplayName}" as version ${created.version}`)
    return { agentId: created.agentId, failedMemories: await persistPendingMemories(created.agentId) }
  }

  const runExclusive = async (save: () => Promise<void>) => {
    if (saveInFlight.current) return
    saveInFlight.current = true
    try {
      await save()
    } finally {
      saveInFlight.current = false
    }
  }

  const finishCreate = ({ agentId: createdAgentId, failedMemories }: { agentId: string; failedMemories: PendingAgentMemoryEntry[] }) => {
    if (failedMemories.length === 0 && guard.isBlockedNow()) {
      guard.leave()
      return
    }
    guard.stay()
    guard.allowNextNavigation()
    if (failedMemories.length === 0) {
      navigate(`${agentsPath}/${createdAgentId}/${currentSection()}`, { replace: true })
      return
    }
    const carried: CarriedAgentState = { pendingMemories: failedMemories }
    navigate(`${agentsPath}/${createdAgentId}/memory`, { replace: true, state: carried })
  }

  const handleSave = () => runExclusive(async () => {
    setSaveAttempted(true)
    if (validationError) return
    if (savedAgent) {
      if (await saveExisting(savedAgent)) setSaveAttempted(false)
      return
    }
    const created = await createNew()
    if (created) finishCreate(created)
  })

  const handleSaveAndLeave = () => runExclusive(async () => {
    if (validationError) return
    if (savedAgent) {
      if (await saveExisting(savedAgent)) guard.leave()
      return
    }
    const created = await createNew()
    if (created) finishCreate(created)
  })

  const handleCancel = () => {
    if (savedAgent) {
      discardDrafts()
      return
    }
    if (isDirty) guard.allowNextNavigation()
    navigate(agentsPath)
  }

  const handleExport = () => {
    downloadBlob(new Blob([editorYaml], { type: 'application/yaml' }), `${trimmedInternalName || 'agent'}.yaml`)
  }

  if (authLoading) return null

  if (!hasPermission(isNew ? Permissions.Project.Agent.Create : Permissions.Project.Agent.Read, projectId)) {
    return <Navigate to={isNew ? agentsPath : `/projects/${projectId}/backlog`} replace />
  }

  if (!isNew && isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground" role="status" aria-live="polite" data-testid="agent-detail-loading">
        Loading agent…
      </div>
    )
  }

  if (!isNew && !savedAgent) {
    return (
      <div className="space-y-4 py-12 text-center" role="alert" data-testid="agent-detail-error">
        <p className="text-sm text-destructive">Couldn't load this agent. It may have been deleted.</p>
        <Button variant="outline" asChild>
          <Link to={agentsPath}>
            <ArrowLeft className="h-4 w-4" />
            Back to Agents
          </Link>
        </Button>
      </div>
    )
  }

  const canEdit = savedAgent ? !savedAgent.isReadOnly && hasPermission(Permissions.Project.Agent.Update, projectId) : true
  const canReadMemory = hasPermission(Permissions.Project.Agent.Update, projectId)

  const context: AgentDetailContext = {
    projectId: projectId!,
    agent: savedAgent,
    canEdit,
    canReadMemory,
    memoryAgentId: savedAgent?.memoryEnabled ? savedAgent.id : null,
    pendingMemories,
    pendingMemoryConflict,
    onQueueMemory: entry => {
      if (pendingMemories.some(pending => pending.type === entry.type && pending.content === entry.content)) {
        return `This ${entry.type.toLowerCase()} is already waiting to be saved with the agent.`
      }
      setPendingMemories(prev => [...prev, { ...entry, key: crypto.randomUUID() }])
      return null
    },
    onRemovePendingMemory: key => setPendingMemories(prev => prev.filter(pending => pending.key !== key)),
    editorYaml,
    onYamlChange: setYamlDraft,
    parsedConfig,
    unknownYamlKeys,
    displayName,
    onDisplayNameChange: value => {
      setDisplayNameDraft(value)
      if (internalNameDraft === null) setInternalNameError(null)
    },
    internalName,
    onInternalNameChange: value => {
      setInternalNameDraft(value)
      setInternalNameError(null)
    },
    internalNameError: internalNameError ?? (saveAttempted && !savedAgent ? internalNameProblem : null),
    showValidation: saveAttempted,
    description,
    onDescriptionChange: setDescriptionDraft,
    missingRequiredFields,
    isDirty,
    isSaving,
    saveBlockedReason,
    onSave: () => void handleSave(),
    onCancel: handleCancel,
    onExport: handleExport,
  }

  return (
    <div className="space-y-6" data-testid="agent-detail-page">
      <div className="min-w-0">
        <p className="text-sm font-medium text-card-foreground">
          Project: {project?.name ?? 'Loading...'}
        </p>
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" className="shrink-0" asChild aria-label="Back to Agents">
            <Link to={agentsPath}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="truncate text-2xl font-bold text-foreground">{savedAgent?.displayName ?? 'New Agent'}</h1>
          {savedAgent?.isGlobal ? (
            <>
              <Badge variant="outline" className="shrink-0 border-primary text-xs" data-testid="agent-category-chip">System</Badge>
              <Badge variant="outline" className="shrink-0 text-xs text-muted-foreground" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="agent-readonly-chip">read-only</Badge>
            </>
          ) : (
            <Badge variant="outline" className="shrink-0 border-primary text-xs" data-testid="agent-category-chip">Custom</Badge>
          )}
        </div>
      </div>

      {isError && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive" role="alert" data-testid="agent-detail-stale">
          Couldn't refresh this agent — it may have been deleted. Anything you have edited here is still unsaved.
        </p>
      )}

      <div className="flex gap-4">
        <AgentSectionsNav showMemory={canReadMemory} />
        <div className="min-w-0 flex-1">
          <Outlet context={context} />
        </div>
      </div>

      <UnsavedChangesDialog
        open={guard.isBlocked}
        isSaving={isSaving}
        saveBlockedReason={isSaving ? null : validationError ?? saveBlockedReason}
        onSave={() => void handleSaveAndLeave()}
        onDiscard={guard.leave}
        onStay={guard.stay}
      />
    </div>
  )
}
